import React, { useState, useEffect, useRef } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { pbEngineer } from '../../lib/pb';
import { 
  GAS_CLASSIFICATION_LABELS, 
  GAS_CLASSIFICATION_SEVERITY,
  CERT_TYPE_LABELS,
  CERT_TYPE_VALIDITY_MONTHS 
} from '../../data/mappers';

// Utility to detect mobile viewport for responsive behavior
const isMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

// Local storage key for autosave drafts
const getAutosaveKey = (certificateType, jobId, engineerId) => {
  return `gas_cert_draft_${certificateType}_${jobId}_${engineerId}`;
};

// Validation rules from gas_certificates_build_spec.md §5
export const validateGasCertificate = (type, form, classification) => {
  const errors = [];
  
  // Common validation for all certificate types
  if (!form.property_address?.trim()) {
    errors.push('Property address is required');
  }
  
  if (!form.engineer_name?.trim()) {
    errors.push('Engineer name is required');
  }
  
  // Gas Safe registration number format validation (GS + 7-8 digits)
  const regNumber = form.engineer_registration_number?.trim();
  if (!regNumber || !/^\d{7,8}$/.test(regNumber)) {
    errors.push('Gas Safe registration number must be 7-8 digits');
  }
  
  if (!form.business_name?.trim()) {
    errors.push('Employing business name is required');
  }
  
  // Type-specific validation
  switch (type) {
    case 'lgsr':
    case 'gsr':
      // Every appliance/flue must have recorded results
      if (!form.appliances || form.appliances.length === 0) {
        errors.push('At least one appliance must be recorded');
      } else {
        form.appliances.forEach((app, idx) => {
          if (!app.make?.trim() && !app.model?.trim() && !app.location?.trim()) {
            errors.push(`Appliance ${idx + 1}: make, model, or location required`);
          }
          if (!app.safe_to_use || app.safe_to_use === '') {
            errors.push(`Appliance ${idx + 1}: safe to use status required`);
          }
        });
      }
      break;
      
    case 'gwn':
      // GWN classification conflict validation
      if (classification === 'safe_to_use') {
        const hasDangerousFault = (form.faults || []).some(
          fault => fault.classification === 'immediately_dangerous'
        );
        if (hasDangerousFault) {
          errors.push('Cannot classify as "Safe to Use" when faults are marked as "Immediately Dangerous"');
        }
      }
      if (!form.faults || form.faults.length === 0) {
        errors.push('At least one fault must be recorded for a Warning Notice');
      }
      break;
      
    case 'gsirep':
      if (!form.inspection_type?.trim()) {
        errors.push('Inspection type is required');
      }
      break;
      
    case 'gmsc':
      if (!form.service_items || form.service_items.length === 0) {
        errors.push('At least one service item must be recorded');
      }
      break;
      
    case 'gbsr':
      if (!form.fault_reported?.trim()) {
        errors.push('Fault reported is required');
      }
      if (!form.work_carried_out?.trim()) {
        errors.push('Work carried out is required');
      }
      break;
      
    case 'wpcr':
      if (!form.test_type?.trim()) {
        errors.push('Test type is required');
      }
      if (!form.test_results?.trim()) {
        errors.push('Test results are required');
      }
      break;
  }
  
  return errors;
};

// Base certificate form component with mobile wizard and desktop flows
// Implements the responsive flow spec from gas_certificate_form_ux_spec.md
export default function CertificateFormBase({ 
  certificateType, 
  jobId, 
  client, 
  initialData = {},
  onSave,
  onCancel,
  isMobile = isMobileViewport()
}) {
  const { data } = useData();
  const { notify } = useToast();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => getInitialFormData(certificateType, initialData));
  const [classification, setClassification] = useState('');
  const [photos, setPhotos] = useState([]);
  const [autosaving, setAutosaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const autosaveTimeoutRef = useRef(null);
  
  // Get form steps based on certificate type
  const steps = getFormSteps(certificateType);
  
  // Autosave effect - saves draft state to localStorage
  useEffect(() => {
    const engineerId = pbEngineer.authStore?.record?.id;
    if (!engineerId || !jobId) return;
    
    const autosaveKey = getAutosaveKey(certificateType, jobId, engineerId);
    
    // Load autosave on mount
    const savedDraft = localStorage.getItem(autosaveKey);
    if (savedDraft && Object.keys(initialData).length === 0) {
      try {
        const draft = JSON.parse(savedDraft);
        setForm(draft.form);
        setClassification(draft.classification || '');
        setPhotos(draft.photos || []);
        if (draft.step !== undefined) {
          setStep(Math.min(draft.step, steps.length - 1));
        }
      } catch (e) {
        console.error('Failed to load autosave:', e);
      }
    }
  }, [certificateType, jobId, initialData, steps.length]);
  
  // Autosave on form changes (debounced)
  useEffect(() => {
    const engineerId = pbEngineer.authStore?.record?.id;
    if (!engineerId || !jobId) return;
    
    const autosaveKey = getAutosaveKey(certificateType, jobId, engineerId);
    
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    setAutosaving(true);
    autosaveTimeoutRef.current = setTimeout(() => {
      const draft = {
        form,
        classification,
        photos,
        step,
        timestamp: new Date().toISOString()
      };
      
      try {
        localStorage.setItem(autosaveKey, JSON.stringify(draft));
        setAutosaving(false);
      } catch (e) {
        console.error('Autosave failed:', e);
        setAutosaving(false);
      }
    }, 2000); // 2 second debounce
    
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [form, classification, photos, step, certificateType, jobId]);
  
  // Update form field
  const setField = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Update nested field (e.g. appliances[0].make)
  const setNestedField = (path, value) => {
    setForm(prev => {
      const newForm = { ...prev };
      const keys = path.split('.');
      let current = newForm;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const match = key.match(/([^[]+)\[(\d+)\]/);
        if (match) {
          const arrayName = match[1];
          const index = parseInt(match[2]);
          if (!current[arrayName]) current[arrayName] = [];
          if (!current[arrayName][index]) current[arrayName][index] = {};
          current = current[arrayName][index];
        } else {
          if (!current[key]) current[key] = {};
          current = current[key];
        }
      }
      
      current[keys[keys.length - 1]] = value;
      return newForm;
    });
  };
  
  // Add item to array (e.g. new appliance, fault)
  const addItem = (arrayName, defaultItem) => {
    setForm(prev => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), defaultItem]
    }));
  };
  
  // Remove item from array
  const removeItem = (arrayName, index) => {
    setForm(prev => {
      const newArray = [...(prev[arrayName] || [])];
      if (newArray.length > 1) {
        newArray.splice(index, 1);
      } else {
        // Keep one empty item
        return { ...prev, [arrayName]: [getEmptyItem(arrayName)] };
      }
      return { ...prev, [arrayName]: newArray };
    });
  };
  
  // Navigation
  const nextStep = () => {
    // Validate current step before proceeding
    const stepErrors = validateCurrentStep(certificateType, step, form, classification);
    if (stepErrors.length > 0) {
      setValidationErrors(stepErrors);
      stepErrors.forEach(err => notify(err, 'error'));
      return false;
    }
    
    setValidationErrors([]);
    if (step < steps.length - 1) {
      setStep(step + 1);
      return true;
    }
    return false;
  };
  
  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };
  
  const goToStep = (newStep) => {
    if (newStep >= 0 && newStep < steps.length) {
      setStep(newStep);
    }
  };
  
  // Handle photo capture
  const handlePhotoCapture = (photoData) => {
    setPhotos(prev => [...prev, {
      id: Date.now(),
      data: photoData,
      timestamp: new Date().toISOString(),
      step: steps[step]?.id || 'unknown'
    }]);
  };
  
  // Handle signature capture (will be provided by parent component)
  const handleSignature = (signatureData) => {
    setField('signature_data', signatureData);
  };
  
  // Final validation and save
  const handleSave = async () => {
    // Full validation
    const errors = validateGasCertificate(certificateType, form, classification);
    if (errors.length > 0) {
      setValidationErrors(errors);
      errors.forEach(err => notify(err, 'error'));
      return false;
    }
    
    setValidationErrors([]);
    setIsSaving(true);
    
    try {
      const engineerId = pbEngineer.authStore?.record?.id;
      // Expiry drives the certificates.pb.js key_dates sync — derive from
      // check date + type validity (same rule the admin form uses).
      const checkDate = form.check_date || form.service_date || form.breakdown_date || form.commissioning_date || new Date().toISOString().split('T')[0];
      const months = CERT_TYPE_VALIDITY_MONTHS[certificateType] || 12;
      const expiry = new Date(`${checkDate}T12:00:00`);
      expiry.setMonth(expiry.getMonth() + months);
      
      await onSave({
        type: certificateType,
        form_data: form,
        classification: classification || undefined,
        photos,
        engineer_id: engineerId,
        job_id: jobId,
        client_id: client?.id,
        issued_date: checkDate,
        expiry_date: expiry.toISOString().split('T')[0],
      });
      
      // Clear autosave on successful save
      if (engineerId && jobId) {
        const autosaveKey = getAutosaveKey(certificateType, jobId, engineerId);
        localStorage.removeItem(autosaveKey);
      }
      
      return true;
    } catch (error) {
      notify(error.message || 'Failed to save certificate', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  };
  
  return {
    // State
    step,
    form,
    classification,
    photos,
    validationErrors,
    isSaving,
    autosaving,
    steps,
    isMobile,
    
    // Actions
    setField,
    setNestedField,
    setClassification,
    setStep,
    nextStep,
    prevStep,
    goToStep,
    addItem,
    removeItem,
    handlePhotoCapture,
    handleSignature,
    handleSave,
    setValidationErrors,
    
    // Data
    client,
    jobId,
    certificateType,
    data
  };
}

// Helper functions

function getInitialFormData(type, initialData = {}) {
  const defaults = {
    property_address: '',
    landlord_name: '',
    landlord_agent: '',
    check_date: new Date().toISOString().split('T')[0],
    engineer_name: '',
    engineer_registration_number: '',
    business_name: '',
    business_address: '',
    business_phone: '',
  };
  
  switch (type) {
    case 'lgsr':
    case 'gsr':
      return {
        ...defaults,
        appliances: initialData.appliances || [getEmptyAppliance()],
        flues: initialData.flues || [],
        general_condition: '',
        installation_satisfactory: false,
        remedial_work_required: '',
        further_investigation_required: '',
        ...initialData
      };
      
    case 'gwn':
      return {
        ...defaults,
        fault_date: new Date().toISOString().split('T')[0],
        fault_time: new Date().toTimeString().slice(0, 5),
        fault_location: '',
        fault_description: '',
        immediate_action_taken: '',
        customer_advised: false,
        customer_agreement: '',
        faults: initialData.faults || [getEmptyFault()],
        ...initialData
      };
      
    case 'gsirep':
      return {
        ...defaults,
        inspection_type: '',
        inspection_reason: '',
        appliances: initialData.appliances || [getEmptyAppliance()],
        findings: '',
        recommendations: '',
        ...initialData
      };
      
    case 'gmsc':
      return {
        ...defaults,
        service_date: new Date().toISOString().split('T')[0],
        appliance_type: '',
        appliance_make: '',
        appliance_model: '',
        service_items: initialData.service_items || [getEmptyServiceItem()],
        operating_pressures: '',
        general_condition: '',
        next_service_date: '',
        ...initialData
      };
      
    case 'gbsr':
      return {
        ...defaults,
        breakdown_date: new Date().toISOString().split('T')[0],
        breakdown_time: new Date().toTimeString().slice(0, 5),
        fault_reported: '',
        diagnosis: '',
        work_carried_out: '',
        parts_used: [],
        labor_hours: 0,
        outcome: '',
        ...initialData
      };
      
    case 'wpcr':
      return {
        ...defaults,
        commissioning_date: new Date().toISOString().split('T')[0],
        system_description: '',
        test_type: '',
        test_pressure: '',
        test_duration: '',
        test_results: '',
        pipework_satisfactory: false,
        next_test_date: '',
        ...initialData
      };
      
    default:
      return defaults;
  }
}

function getEmptyAppliance() {
  return {
    make: '',
    model: '',
    type: '',
    location: '',
    flue_type: '',
    flue_location: '',
    heat_input_kw: '',
    heat_output_kw: '',
    safe_to_use: '',
    defects: '',
    remedial_action: '',
    inspector_comments: ''
  };
}

function getEmptyFault() {
  return {
    location: '',
    description: '',
    classification: '',
    immediate_action: '',
    further_action_required: false
  };
}

function getEmptyServiceItem() {
  return {
    item: '',
    condition: '',
    action_taken: '',
    satisfactory: false
  };
}

function getEmptyItem(arrayName) {
  switch (arrayName) {
    case 'appliances': return getEmptyAppliance();
    case 'faults': return getEmptyFault();
    case 'service_items': return getEmptyServiceItem();
    default: return {};
  }
}

function getFormSteps(type) {
  const commonSteps = [
    { id: 'property', title: 'Property', description: 'Property details' },
    { id: 'engineer', title: 'Engineer', description: 'Engineer details' },
    { id: 'summary', title: 'Summary', description: 'Review and submit' }
  ];
  
  switch (type) {
    case 'lgsr':
    case 'gsr':
      return [
        ...commonSteps.slice(0, 2),
        { id: 'appliances', title: 'Appliances', description: 'Appliance checks' },
        { id: 'flues', title: 'Flues', description: 'Flue checks' },
        { id: 'classification', title: 'Classification', description: 'Safety classification' },
        ...commonSteps.slice(2)
      ];
      
    case 'gwn':
      return [
        ...commonSteps.slice(0, 2),
        { id: 'faults', title: 'Faults', description: 'Identified faults' },
        { id: 'classification', title: 'Classification', description: 'Safety classification' },
        { id: 'actions', title: 'Actions', description: 'Actions taken' },
        ...commonSteps.slice(2)
      ];
      
    case 'gsirep':
      return [
        ...commonSteps.slice(0, 2),
        { id: 'inspection', title: 'Inspection', description: 'Inspection details' },
        { id: 'appliances', title: 'Appliances', description: 'Appliance findings' },
        ...commonSteps.slice(2)
      ];
      
    case 'gmsc':
      return [
        ...commonSteps.slice(0, 2),
        { id: 'appliance', title: 'Appliance', description: 'Appliance details' },
        { id: 'service', title: 'Service', description: 'Service checklist' },
        ...commonSteps.slice(2)
      ];
      
    case 'gbsr':
      return [
        ...commonSteps.slice(0, 2),
        { id: 'fault', title: 'Fault', description: 'Fault details' },
        { id: 'diagnosis', title: 'Diagnosis', description: 'Diagnosis and work' },
        { id: 'parts', title: 'Parts', description: 'Parts used' },
        ...commonSteps.slice(2)
      ];
      
    case 'wpcr':
      return [
        ...commonSteps.slice(0, 2),
        { id: 'system', title: 'System', description: 'System details' },
        { id: 'tests', title: 'Tests', description: 'Test results' },
        ...commonSteps.slice(2)
      ];
      
    default:
      return commonSteps;
  }
}

function validateCurrentStep(type, step, form, classification) {
  const errors = [];
  
  switch (type) {
    case 'lgsr':
    case 'gsr':
      if (step === 0) { // Property
        if (!form.property_address?.trim()) {
          errors.push('Property address is required');
        }
      } else if (step === 1) { // Engineer
        if (!form.engineer_name?.trim()) {
          errors.push('Engineer name is required');
        }
        const regNumber = form.engineer_registration_number?.trim();
        if (!regNumber || !/^\d{7,8}$/.test(regNumber)) {
          errors.push('Gas Safe registration number must be 7-8 digits');
        }
      } else if (step === 2) { // Appliances
        if (!form.appliances || form.appliances.length === 0) {
          errors.push('At least one appliance is required');
        }
      }
      break;
      
    case 'gwn':
      if (step === 0) { // Property
        if (!form.property_address?.trim()) {
          errors.push('Property address is required');
        }
      } else if (step === 1) { // Engineer
        if (!form.engineer_name?.trim()) {
          errors.push('Engineer name is required');
        }
        const regNumber = form.engineer_registration_number?.trim();
        if (!regNumber || !/^\d{7,8}$/.test(regNumber)) {
          errors.push('Gas Safe registration number must be 7-8 digits');
        }
      } else if (step === 2) { // Faults
        if (!form.faults || form.faults.length === 0) {
          errors.push('At least one fault must be recorded');
        }
      } else if (step === 3) { // Classification
        if (!classification) {
          errors.push('Safety classification is required');
        }
      }
      break;
  }
  
  return errors;
}